<script setup lang="ts">
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { reviewProjectRoute } from '@/router/links';

/**
 * The trail every review-project page opens with: Review → Review Projects →
 * the project → the page. Each page used to spell its own out, which is how a
 * crumb ends up clickable on one page and inert on the next.
 *
 * A crumb is a link exactly when there is somewhere further along the trail to
 * come back from, so the last one rendered is always plain text.
 *
 * Crumbs only, no breadcrumb of its own: mount it inside a layout's
 * `#breadcrumb` slot, which supplies the bar and the `GaiaBreadcrumb`.
 */
const props = defineProps<{
  /** Omit on the list page — there the list itself is where you are. */
  projectUid?: string;
  /** The project's name, or undefined while it is still loading. */
  projectName?: string;
  /** A sub page's name. With one, the project crumb links to the workspace. */
  page?: string;
}>();
</script>

<template>
  <n-breadcrumb-item>{{ navLabels.review }}</n-breadcrumb-item>
  <n-breadcrumb-item v-if="props.projectUid" clickable>
    <router-link :to="{ name: 'review-projects' }">{{ navLabels.reviewProjects }}</router-link>
  </n-breadcrumb-item>
  <n-breadcrumb-item v-else>{{ navLabels.reviewProjects }}</n-breadcrumb-item>
  <template v-if="props.projectUid">
    <n-breadcrumb-item v-if="props.page" clickable>
      <router-link :to="reviewProjectRoute(props.projectUid)">
        {{ props.projectName ?? '…' }}
      </router-link>
    </n-breadcrumb-item>
    <n-breadcrumb-item v-else>{{ props.projectName ?? '…' }}</n-breadcrumb-item>
  </template>
  <n-breadcrumb-item v-if="props.page">{{ props.page }}</n-breadcrumb-item>
</template>
